import { useCallback, useMemo, useState } from "react";
import { Button, Checkbox, type Column } from "@legal-500/legal500-design-system";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal.js";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import {
  APPLY_CHUNK_SIZE,
  officeSlugBackfillService,
  type ApplyRowResult,
  type ScanResponse,
  type SkipReason,
} from "../../../api/officeSlugBackfill.service.js";
import { downloadRollbackCsv } from "./rollbackCsv.js";
import { ResultsTable, countByStatus } from "./ResultsTable.js";
import { runChunkedApply } from "./runApply.js";
import { RollbackSection } from "../OfficeIdentityBackfill/RollbackSection.js";
import styles from "./OfficeSlugBackfill.module.css";

/**
 * Generates URL slugs for the ~85 address-book offices created before the
 * slug generator existed (SOFTSUPP-333 follow-up). A NULL slug makes an
 * office invisible to a paid product, so the scan previews the slug the
 * generator would produce for each slugless office and apply writes the
 * approved selection.
 *
 * The flow mirrors the neighbouring Office Identity Backfill tool: scan is
 * read-only and shows every planned slug first, and apply is a separate,
 * explicit, confirmed step. Offices the generator cannot plan (a skip reason
 * instead of a planned slug) are shown for the record but can never be
 * selected or applied. Everything plannable starts selected — the missing
 * slug is the bug, so the safe default is to fix it.
 *
 * The rollback CSV offered after the run lists every office that received a
 * slug, in the Office Identity Backfill restore format with an empty restore
 * value. That tool's RollbackSection is embedded at the bottom of this page,
 * so a bad run is undone right here: upload the CSV, preview, apply restore.
 * The restore still goes through the office-identity-backfill API — this page
 * adds no second write path.
 */

type Phase = "idle" | "scanning" | "scanned" | "applying" | "done";

type ApplyProgress = {
  done: number;
  total: number;
};

type TableRow = {
  id: string;
  position: number;
  officeId: string;
  officeName: string;
  companyName: string;
  city: string | null;
  countryName: string | null;
  plannedSlug: string | null;
  skipReason: SkipReason | null;
  selectable: boolean;
  selected: boolean;
};

const SKIP_REASON_LABELS: Record<SkipReason, string> = {
  no_company_slug: "no company slug",
  unslugifiable: "unslugifiable",
};

/**
 * The header checkbox, which is tri-state: some-but-not-all selected reads as
 * indeterminate. The design system's Checkbox has no prop for that — it is a
 * DOM property, not an attribute — so it is set through the forwarded ref.
 */
function SelectAllCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onChange: () => void;
}) {
  const setInput = useCallback(
    (input: HTMLInputElement | null) => {
      if (input) {
        input.indeterminate = indeterminate;
      }
    },
    [indeterminate],
  );
  return (
    <Checkbox
      ref={setInput}
      aria-label="Select all plannable rows"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

export default function OfficeSlugBackfill() {
  const { hasPermission } = usePermissions();

  const [phase, setPhase] = useState<Phase>("idle");
  const [scan, setScan] = useState<ScanResponse | null>(null);
  /** Office ids — the scan returns one row per office. */
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [applyProgress, setApplyProgress] = useState<ApplyProgress>({ done: 0, total: 0 });
  /** The applied run's results, kept for the rollback download. */
  const [applied, setApplied] = useState<ApplyRowResult[] | null>(null);

  // A developer tool with a bulk blast radius, gated on developer.write like
  // its Developer-section neighbour, Office Identity Backfill.
  const canUse = hasPermission("developer.write");

  const runScan = useCallback(async () => {
    setError(null);
    setApplied(null);
    setScan(null);
    setSelected(new Set());
    setPhase("scanning");
    try {
      const response = await officeSlugBackfillService.scan();
      setScan(response);
      // Every plannable office starts selected — the missing slug is the bug.
      setSelected(
        new Set(response.rows.filter(row => row.plannedSlug !== null).map(row => row.officeId)),
      );
      setPhase("scanned");
    } catch (scanError) {
      setError(scanError instanceof Error ? scanError.message : String(scanError));
      setPhase("idle");
    }
  }, []);

  const toggleRow = useCallback((officeId: string) => {
    setSelected(current => {
      const next = new Set(current);
      if (!next.delete(officeId)) {
        next.add(officeId);
      }
      return next;
    });
  }, []);

  const setRows = useCallback((officeIds: string[], isSelected: boolean) => {
    setSelected(current => {
      const next = new Set(current);
      for (const officeId of officeIds) {
        if (isSelected) {
          next.add(officeId);
        } else {
          next.delete(officeId);
        }
      }
      return next;
    });
  }, []);

  const scanRows = scan?.rows ?? [];

  /** The offices this run will write: plannable and still ticked. */
  const selectedIds = useMemo(
    () =>
      scanRows
        .filter(row => row.plannedSlug !== null && selected.has(row.officeId))
        .map(row => row.officeId),
    [scanRows, selected],
  );
  const selectedCount = selectedIds.length;
  const plannableCount = useMemo(
    () => scanRows.filter(row => row.plannedSlug !== null).length,
    [scanRows],
  );

  const applySlugs = useCallback(async () => {
    setIsConfirming(false);
    setError(null);
    setPhase("applying");

    const { results, failure } = await runChunkedApply(selectedIds, (done, total) =>
      setApplyProgress({ done, total }),
    );

    if (results.length === 0 && failure) {
      // Nothing came back at all, so there is no rollback data to protect and
      // no result worth showing: back to the preview with the selection intact.
      setError(failure);
      setPhase("scanned");
      return;
    }
    setApplied(results);
    setError(failure);
    setPhase("done");
  }, [selectedIds]);

  /** Office names for the result table and rollback CSV, from the scan. */
  const nameById = useMemo(() => {
    const names = new Map<string, string>();
    for (const row of scanRows) {
      names.set(row.officeId, row.officeName);
    }
    return names;
  }, [scanRows]);

  const nameFor = useCallback(
    (result: ApplyRowResult) => nameById.get(result.officeId) ?? null,
    [nameById],
  );

  const tableRows = useMemo<TableRow[]>(
    () =>
      scanRows.map((row, index) => ({
        id: row.officeId,
        position: index + 1,
        officeId: row.officeId,
        officeName: row.officeName,
        companyName: row.companyName,
        city: row.city,
        countryName: row.countryName,
        plannedSlug: row.plannedSlug,
        skipReason: row.skipReason,
        selectable: row.plannedSlug !== null,
        selected: selected.has(row.officeId),
      })),
    [scanRows, selected],
  );

  const selectableRows = useMemo(() => tableRows.filter(row => row.selectable), [tableRows]);
  const allSelected = selectableRows.length > 0 && selectedCount === selectableRows.length;

  const columns = useMemo<Column<TableRow>[]>(
    () => [
      {
        header: (
          <SelectAllCheckbox
            checked={allSelected}
            indeterminate={selectedCount > 0 && !allSelected}
            disabled={selectableRows.length === 0}
            onChange={() =>
              setRows(
                selectableRows.map(row => row.officeId),
                !allSelected,
              )
            }
          />
        ) as unknown as string,
        accessor: "selected",
        render: (_value, row) =>
          row.selectable ? (
            <Checkbox
              aria-label={`Backfill row ${row.position} — ${row.officeName} (${row.companyName})`}
              checked={row.selected}
              onChange={() => toggleRow(row.officeId)}
            />
          ) : (
            <span className={styles.mono} aria-hidden="true">
              —
            </span>
          ),
      },
      {
        header: "Company",
        accessor: "companyName",
        wrap: true,
        render: (_value, row) => <span className={styles.name}>{row.companyName || "—"}</span>,
      },
      {
        header: "Office",
        accessor: "officeName",
        wrap: true,
        render: (_value, row) => <span className={styles.name}>{row.officeName || "—"}</span>,
      },
      {
        header: "City",
        accessor: "city",
        wrap: true,
        render: (_value, row) => <span className={styles.name}>{row.city || "—"}</span>,
      },
      {
        header: "Country",
        accessor: "countryName",
        wrap: true,
        render: (_value, row) => <span className={styles.name}>{row.countryName ?? "—"}</span>,
      },
      {
        header: "Planned slug",
        accessor: "plannedSlug",
        wrap: true,
        render: (_value, row) =>
          row.plannedSlug === null ? (
            <span className={`${styles.pill} ${styles.pillWarning}`}>
              {row.skipReason ? SKIP_REASON_LABELS[row.skipReason] : "skipped"}
            </span>
          ) : (
            <span className={styles.mono}>{row.plannedSlug}</span>
          ),
      },
    ],
    [allSelected, selectedCount, selectableRows, setRows, toggleRow],
  );

  const resultCounts = useMemo(() => countByStatus(applied ?? []), [applied]);
  const generatedCount = resultCounts("generated");
  const skippedCount =
    resultCounts("skipped_not_empty") +
    resultCounts("skipped_no_company_slug") +
    resultCounts("skipped_not_ab_native") +
    resultCounts("skipped_unslugifiable");
  const notFoundCount = resultCounts("not_found");

  if (!canUse) {
    return (
      <div className={styles.page}>
        <div className={styles.accessDenied} role="alert">
          <h2>Access Denied</h2>
          <p>You do not have permission to view the Office Slug Backfill tool.</p>
        </div>
      </div>
    );
  }

  const isBusy = phase === "scanning" || phase === "applying";
  const skippedTotal = scan
    ? scan.counts.skippedNoCompanySlug + scan.counts.skippedUnslugifiable
    : 0;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Office Slug Backfill"
        subtitle="Generate URL slugs for address-book offices created before the slug generator existed"
      />

      {error ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          {error}
        </div>
      ) : null}

      <div className={styles.card}>
        <div className={styles.cardTitle}>1 · Scan for offices without a slug</div>
        <div className={styles.toolbar}>
          <Button variant="contained" disabled={isBusy} onClick={() => void runScan()}>
            Scan
          </Button>
          <span className={styles.rowNote}>
            Read-only: finds offices with no slug and previews the slug the generator would produce.
            Nothing is written until you apply.
          </span>
        </div>
      </div>

      {phase === "scanning" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Scanning…</div>
        </div>
      ) : null}

      {phase === "applying" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Applying…</div>
          <div className={styles.progress} role="status" aria-live="polite">
            {`${applyProgress.done.toLocaleString()} / ${applyProgress.total.toLocaleString()} offices`}
          </div>
          <div className={styles.rowNote}>
            {applyProgress.total > APPLY_CHUNK_SIZE
              ? `Sent in batches of ${APPLY_CHUNK_SIZE.toLocaleString()}. Keep this tab open — the rollback CSV is built from the responses.`
              : "Keep this tab open — the rollback CSV is built from the responses."}
          </div>
        </div>
      ) : null}

      {phase === "scanned" && scan ? (
        <>
          <div className={styles.card}>
            <div className={styles.cardTitle}>2 · Review planned slugs</div>
            <div className={styles.tiles}>
              <div className={`${styles.tile} ${styles.tileNeutral}`}>
                <div className={styles.tileNumber}>{scan.counts.plannable}</div>
                <div className={styles.tileLabel}>Plannable</div>
              </div>
              <div className={`${styles.tile} ${styles.tileError}`}>
                <div className={styles.tileNumber}>{scan.counts.skippedNoCompanySlug}</div>
                <div className={styles.tileLabel}>Skipped — no company slug</div>
              </div>
              <div className={`${styles.tile} ${styles.tileError}`}>
                <div className={styles.tileNumber}>{scan.counts.skippedUnslugifiable}</div>
                <div className={styles.tileLabel}>Skipped — unslugifiable</div>
              </div>
              <div className={`${styles.tile} ${styles.tileOk}`}>
                <div className={styles.tileNumber}>{selectedCount}</div>
                <div className={styles.tileLabel}>Selected — will get a slug</div>
              </div>
            </div>
          </div>

          {skippedTotal > 0 ? (
            <div className={`${styles.banner} ${styles.bannerWarning}`}>
              {skippedTotal} office{skippedTotal === 1 ? "" : "s"} cannot be slugged — shown for the
              record, excluded from the run.
            </div>
          ) : null}

          <div className={styles.stack}>
            <div className={styles.toolbar}>
              <span className={styles.resultCount} aria-live="polite">
                {selectedCount} of {plannableCount} plannable row
                {plannableCount === 1 ? "" : "s"} selected
              </span>
            </div>
            <DataTable data={tableRows} columns={columns} emptyMessage="Nothing to backfill." />
          </div>

          <div className={styles.toolbar}>
            <span className={styles.spacer} />
            <Button
              variant="contained"
              disabled={selectedCount === 0 || isBusy}
              onClick={() => setIsConfirming(true)}
            >
              Apply {selectedCount} selected
            </Button>
          </div>
        </>
      ) : null}

      {phase === "done" && applied ? (
        <>
          {notFoundCount > 0 ? (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
              {notFoundCount} office{notFoundCount === 1 ? "" : "s"} came back not found — re-scan
              before retrying.
            </div>
          ) : null}

          <div className={styles.card}>
            <div className={styles.cardTitle}>Result</div>
            <div className={styles.tiles}>
              <div className={`${styles.tile} ${styles.tileOk}`}>
                <div className={styles.tileNumber}>{generatedCount}</div>
                <div className={styles.tileLabel}>Generated</div>
              </div>
              <div className={`${styles.tile} ${styles.tileNeutral}`}>
                <div className={styles.tileNumber}>{skippedCount}</div>
                <div className={styles.tileLabel}>Skipped</div>
              </div>
              <div className={`${styles.tile} ${styles.tileError}`}>
                <div className={styles.tileNumber}>{notFoundCount}</div>
                <div className={styles.tileLabel}>Not found</div>
              </div>
            </div>
          </div>

          <ResultsTable results={applied} nameFor={nameFor} />

          <div className={styles.toolbar}>
            <Button variant="outlined" onClick={() => void runScan()}>
              Scan again
            </Button>
            <span className={styles.rowNote}>
              To undo the run, upload the rollback CSV in the Rollback section at the bottom of this
              page.
            </span>
            <span className={styles.spacer} />
            <Button
              variant="contained"
              disabled={generatedCount === 0}
              onClick={() => {
                if (applied) {
                  downloadRollbackCsv(applied, nameFor, new Date());
                }
              }}
            >
              Download rollback CSV
            </Button>
          </div>
        </>
      ) : null}

      <RollbackSection />

      {isConfirming ? (
        <ConfirmationModal
          title="Generate office slugs"
          message={`${selectedCount} office${selectedCount === 1 ? "" : "s"} will get a generated slug.`}
          warningMessage={
            "The rollback CSV offered after the run is the only undo — upload it in the " +
            "Rollback section at the bottom of this page to clear the generated slugs."
          }
          confirmButtonText={`Generate ${selectedCount} slug${selectedCount === 1 ? "" : "s"}`}
          // A stray click beside the dialog must not stand in for a decision
          // about dozens of office rows — Cancel and Escape still do.
          backdropClick={false}
          onClose={() => setIsConfirming(false)}
          onConfirm={applySlugs}
        />
      ) : null}
    </div>
  );
}
